/* select_options.control_adapter.js */

const $ = require("jquery");
const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const util = require("util");
const q = require("q");
const StyleUtil = require("../StyleUtil");

/**
 * @event SelectOptionsControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class SelectOptionsControlAdapter
 * @classdesc renders the Select Options Jtype.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires SelectOptionsControlAdapter#ValueChanged
 */
module.exports = function SelectOptionsAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    self._removedProperties = [];
    var loadedDeferred = q.defer();
    //call Event Emitter Constructor
    EventEmitter.call(this);
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
    var _scope;
    var _oldValue;
    let _oldInvalidStatus = false;
    var _options = {};
    let _required = false;
    let _validationMessages = {};
    let requiredValidationVerbiage = "Please select an option.";
    let _selectOptionsJTypeContext = {};

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/

    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    /**
     * The selection has changed
     * @param newSelection {string} selection name to switch to
     * @param oldSelection {string} current selection name
     * @param configData {object} The configData associated with this selection
     * @private
     */
    function _selectionChanged(newSelection, oldSelection, configData) {
        if (_options.hasOwnProperty(newSelection) || _.isNull(newSelection)) {
            var oldCs = _scope.FormRendererApi.getConfigurationSettings();
            var oldCd = _scope.FormRendererApi.getConfigurationData();
            var cs;

            //Handle deselection
            if (_.isNull(newSelection)) {
                cs = {};
                self.tearDown();
            } else {
                cs = _options[newSelection].value;
            }

            _clearJTypeConfigStylesAndLayouts(oldSelection);
            _updateLayoutSettingsOnChangeSelection(newSelection, oldSelection);
            _scope.FormRendererApi.render(
                cs,
                {
                    configurationData: configData,
                    refInstances: formCtrlDef.refInstances,
                    refTypes: formCtrlDef.refTypes,
                },
                true
            );
            _renderLayoutAndStyles(newSelection);

            //If the 2 selections have the same CS/CD the sub form renderer value change will not fire
            //Because a selection change regardless of sub value will cause this ctrl adapter to have a different value we should fire a value changed.
            if (_.isEqual(configData, oldCd) && _.isEqual(cs, oldCs)) {
                _fireValueChanged();
            }
            _updateValidityStatus();
        } else {
            logger.warn(
                {selection: newSelection},
                "(SelectOptions._changeSelection) Cannot change the selection to one that does not exist."
            );
            loadedDeferred.resolve();
        }
    }

    function _clearJTypeConfigStylesAndLayouts(oldSelection) {
        let oldStyleConfig = _.get(_options, [oldSelection, "Display", "styles"], {});
        let selectionOptionsRendererElem = containingElem.find(
            ".form_entry > .select_options_contents > .select_options_renderer"
        );
        StyleUtil.clearJTypeClassesDynamicallyAdded(
            selectionOptionsRendererElem[0],
            oldStyleConfig,
            _.get(_options[oldSelection], "jTypeContext.layout.container.cssClasses", [])
        );
    }

    function _updateLayoutSettingsOnChangeSelection(selectedOption) {
        let selectedOptionfcd = _options[selectedOption];
        if (selectedOptionfcd && Object.keys(selectedOptionfcd).length > 0) {
            if (!_.has(selectedOptionfcd, "jTypeContext")) {
                let jTypeContextOfNewSelection = StyleUtil.processLayoutConfig(
                    selectedOptionfcd,
                    _selectOptionsJTypeContext,
                    StyleUtil.DEFAULT_INLINE_CLASS,
                    selectedOption
                );
                jTypeContextOfNewSelection.formDepth = _scope.jTypeContext.formDepth;
                selectedOptionfcd.jTypeContext = jTypeContextOfNewSelection;
            }
            _scope.jTypeContext = selectedOptionfcd.jTypeContext;
        } else {
            logger.warn(
                {selection: selectedOption},
                "(SelectOptions._updateLayoutSettingsOnChangeSelection) Cannot update the layout settings for a selection that does not exist."
            );
        }
    }

    function _renderLayoutAndStyles(newSelection) {
        let selectedOptionfcd = _options[newSelection];
        if (selectedOptionfcd && Object.keys(selectedOptionfcd).length > 0) {
            _updateLayoutClassesOnDom(
                _.get(selectedOptionfcd.jTypeContext, "layout.container.cssClasses", [])
            );
            let styleConfig = _.get(selectedOptionfcd, "Display.styles", {});
            let selectionOptionsRendererElem = containingElem.find(
                ".form_entry > .select_options_contents > .select_options_renderer"
            );
            StyleUtil.renderStylesConfig(styleConfig, selectionOptionsRendererElem[0]);
        } else {
            logger.warn(
                {selection: newSelection},
                "(SelectOptions._renderLayoutAndStyles) Cannot render the layout and styles for a selection that does not exist."
            );
        }
    }

    function _updateLayoutClassesOnDom(currentSelectionLayoutClasses) {
        let selectionOptionsRendererElem = containingElem.find(
            ".form_entry > .select_options_contents > .select_options_renderer"
        );
        StyleUtil.appendClassListToElement(
            currentSelectionLayoutClasses,
            selectionOptionsRendererElem[0]
        );
    }

    /**
     * Fire the Value Changed Event
     * @private
     */
    function _fireValueChanged() {
        //Get the new Value
        var newVal = self.getValue();
        self.emit("ValueChanged", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldValue: _oldValue,
            newValue: newVal,
        });
        _oldValue = newVal;
    }

    /**
     * Fire the Invalid status changed event
     * @private
     */
    function _fireInvalidStatusChanged(newInvalidStatus) {
        if (newInvalidStatus !== _oldInvalidStatus) {
            self.emit("Invalid", {
                name: formCtrlDef.name,
                containingElem: containingElem,
                controlAdapter: self,
                formControlDefinition: formCtrlDef,
                oldValue: _oldInvalidStatus,
                newValue: newInvalidStatus,
            });
            _oldInvalidStatus = newInvalidStatus;
        }
    }

    function _updateValidityStatus() {
        let newInvalidStatus = self.isInvalid();
        if (newInvalidStatus) {
            if (self._isRequiredValidationFailingAtSelectOptionsLevel()) {
                _validationMessages = [requiredValidationVerbiage];
                _scope.SelectOptions.selection.api.setInvalid(_validationMessages[0]);
            }
        } else {
            _validationMessages = [];
            _scope.SelectOptions.selection.api.setValid();
        }
        _scope.$apply();
        _fireInvalidStatusChanged(newInvalidStatus);
    }

    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/
    //The name of the control
    this.name = formCtrlDef.name;

    /**
     * Return true if the component is multi edit active.
     * @returns {boolean}
     */
    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            this.setReadOnly(!flag);
            _scope.FormRendererApi.setUnderlingsMultiEditActive(flag);
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        var val = formCtrlDef.value;
        if (_scope.FormRendererApi) {
            var subValues = _scope.FormRendererApi.getValue();
            var parentVal = _scope.SelectOptions.selection.api.getValue();
            val = {};
            val[formCtrlDef.name] = null;
            if (parentVal) {
                val[formCtrlDef.name] = parentVal;
                val = _.extend({}, subValues, val);
            }
        }
        return val;
    };

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (val) {
        var value = _.cloneDeep(val);
        if (_.isPlainObject(value)) {
            var found = value[formCtrlDef.name];

            //update the rendered part
            delete value[formCtrlDef.name];
            let oldSelection = _scope.SelectOptions.selection.api.getValue();
            _scope.SelectOptions.selection.api.setValue(found);
            //render selection
            _selectionChanged(found, oldSelection, value);
            //update oldValue
            _oldValue = self.getValue();
        }

        return self.getValue();
    };

    /**
     * Returns if data on the control is invalid
     * @returns {boolean}
     */
    this.isInvalid = function () {
        if (this._isCurrentlyReadOnly()) {
            return false;
        }
        let requiredViolation = this._isRequiredValidationFailingAtSelectOptionsLevel();
        return (
            requiredViolation ||
            (_scope.FormRendererApi ? _scope.FormRendererApi.isInvalid() : false)
        );
    };

    this._isRequiredValidationFailingAtSelectOptionsLevel = function () {
        return _required ? _.isEmpty(this.getValue()[formCtrlDef.name]) : false;
    };

    this._isCurrentlyReadOnly = function () {
        if (this._multiEditMode) {
            return this._multiEditActive ? this._readOnlyField : true;
        } else {
            return this._readOnlyField;
        }
    };

    this.getValidationMessages = function () {
        let validationMessages = {};
        if (this._isRequiredValidationFailingAtSelectOptionsLevel()) {
            validationMessages[formCtrlDef.name] = [requiredValidationVerbiage];
        } else {
            validationMessages = _scope.FormRendererApi
                ? _scope.FormRendererApi.getValidationMessages()
                : {};
        }
        return validationMessages;
    };

    /**
     * Get the path properties that are not currently selected.
     */
    this.getNonSelectedPaths = function (path) {
        let allPossibleProps = [];
        for (let property in _options) {
            allPossibleProps = _.union(
                allPossibleProps,
                _.get(_options[property], "value.@propertyOrder", [])
            );
        }
        let currentCS = _scope.FormRendererApi.getConfigurationSettings();
        let currentProps = _.get(currentCS, "@propertyOrder", []);
        let nonSelectedProps = _.difference(allPossibleProps, currentProps);
        let pathExtension = "";
        if (!_.isEmpty(path)) {
            pathExtension = path + ".";
        }
        let nonSelectedPaths = [];
        nonSelectedProps.forEach((prop) => {
            nonSelectedPaths.push(pathExtension + prop);
        });
        return nonSelectedPaths;
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        //Do Nothing the dom is cleaned up, the scope is destroyed for us and any listeners
        //on self are unregistered automatically
    };

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angular is before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @param $templateCache {$templateCache}
     */
    this.$init = function ($scope, $templateCache) {
        _scope = $scope;
        $scope.formCtrlDef = formCtrlDef;
        var opts = [];

        _selectOptionsJTypeContext = $scope.jTypeContext;
        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        // Apply item layout styles - these styles customize how this boolean element is placed in parent layout
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        StyleUtil.appendClassListToElement(
            $scope.jTypeContext.layout.inlineLabels,
            containingElem[0]
        );

        var selectOpts = formCtrlDef.configSettings.Select.options;
        selectOpts["@propertyOrder"].forEach(function (optionName) {
            var label = optionName;
            if (selectOpts[optionName].Display && selectOpts[optionName].Display.label) {
                label = selectOpts[optionName].Display.label;
            }
            if (selectOpts[optionName].fieldType === "Object") {
                _options[optionName] = selectOpts[optionName];
                opts.push({label: label, value: optionName});
            }
        });

        //Check if required field in validation object - if so, then do not allow user to select *none*
        if (formCtrlDef.validationInfo && formCtrlDef.validationInfo.required) {
            _required = true;
        }

        //Setup Settings Object
        $scope.SelectOptions = {
            selection: {
                api: {},
                settings: {
                    title: formCtrlDef.displayInfo.tooltip,
                    placeholder: formCtrlDef.displayInfo.placeholder,
                    disableAutoSelect: true,
                    allowSingleDeselect: !_required,
                    options: opts,
                    sort: _.get(formCtrlDef, "configSettings.Display.sort", true),
                    onUserChange: function (newVal, oldVal) {
                        _selectionChanged(newVal, oldVal, {});
                    },
                },
            },
        };

        //Setup directive
        var template = $templateCache.get("templatecache/templates/select_options.template.html");
        var selectElem = $(template);
        containingElem.append(selectElem);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });
        return loadedDeferred.promise;
    };
    this.$init.$inject = ["$scope", "$templateCache"];

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope) {
        this._readOnlyField = false;
        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
        }

        var initialVal;
        if (
            _.isPlainObject(formCtrlDef.configData) &&
            !_.isEmpty(formCtrlDef.configData) &&
            _.get(formCtrlDef.configData, formCtrlDef.name)
        ) {
            initialVal = formCtrlDef.configData;
        } else if (_.get(formCtrlDef, "configSettings.value")) {
            if (!this._multiEditMode) {
                initialVal = {};
                initialVal[formCtrlDef.name] = formCtrlDef.configSettings.value;
            }
        }
        if (initialVal) {
            //Update used/available based on configData
            self.setValue(initialVal);
            $scope.FormRendererApi.once("AllControlsLoaded", () => {
                if (this._readOnlyField) {
                    this.setReadOnly(true);
                }
                if (this._multiEditMode) {
                    this.setMultiEditActive(false);
                }

                loadedDeferred.resolve();
            });
        } else {
            if (this._multiEditMode) {
                this.setMultiEditActive(false);
            }
            loadedDeferred.resolve();
        }

        _updateValidityStatus();
        //Hook up sub form renderer
        //Form Renderer Value changed.
        $scope.FormRendererApi.on("ValueChanged", function (/*evt*/) {
            _fireValueChanged();
        });
        $scope.FormRendererApi.on("Invalid", function (/*evt*/) {
            _updateValidityStatus();
        });
    };

    this.setReadOnly = function (flag) {
        if (flag) {
            _scope.SelectOptions.selection.api.disable();
        } else {
            _scope.SelectOptions.selection.api.enable();
        }
        _scope.FormRendererApi.setReadOnly(flag);
        _updateValidityStatus();
    };

    this.$compiledAndLinked.$inject = ["$scope"];

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
